import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { BrowserMetricViewName } from '@helpcenter/constants';
import { getEmojiProvider } from '@helpcenter/emoji-provider';
import { isUseNewEditorCardProviderEnabled } from '@helpcenter/feature-flags';
import type { LoadableLoadingComponentProps } from '@helpcenter/lazy';
import {
    PortalCardClient,
    PortalAutoformattingProvider,
    PortalCardProviderNew,
    PortalCardProviderDeprecated,
} from '@helpcenter/smart-card';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { withBrowserMetricSubmitter } from 'view/browser-metric';
import ErrorBoundaryMessage from 'view/common/error-boundary-message';
import { MediaWrapper } from 'view/common/media-client-wrapper';
import type { FieldConfigs } from 'view/common/mention-provider';
import { getMentionProvider } from 'view/common/mention-provider';
import { WithUploadCredentials } from 'view/common/with-upload-credentials';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { EditorActions, EditorProps } from '@atlaskit/editor-core';
import { CollapsedEditor, Editor as FabricEditor, WithEditorActions } from '@atlaskit/editor-core';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { MediaOptions } from '@atlaskit/editor-plugins/media/types';
import { SmartCardProvider } from '@atlaskit/link-provider';

import { token } from '@atlaskit/tokens';

export interface MediaContext {
    portalId: number;
    requestId?: number;
}

export interface RichTextEditorActions {
    clear: () => void;
    replaceDocument: (text: string) => void;
    focus: () => void;
    /**
     * Asynchronously retrieves the current rich text content from the editor as a structured JSON document.
     *
     * @returns A Promise that resolves to a JSONDocNode containing the editor's current state
     * in Atlassian Document Format (ADF), which represents rich text content including
     * formatting, mentions, media, and other editor elements in a standardized JSON structure.
     *
     * @remarks This API is exposed by editor-core through editor actions and is used
     * internally for save operations and change detection.
     */
    getValue: () => Promise<JSONDocNode>;
}

type RichTextEditorType = 'comment-editor' | 'field';

type EditorView = Parameters<Required<EditorProps>['onSave']>[0];

export interface RichTextEditorProps {
    editorType: RichTextEditorType;
    placeholderWhenCollapsed?: string;
    placeholderWhenExpanded?: string;
    isExpanded: boolean;
    disabled: boolean;
    isEmojiEnabled: boolean;
    defaultValue?: string;
    expandEditor?: () => void;
    onSave?: (comment: JSONDocNode, actions: RichTextEditorActions) => void;
    onChange?: (comment: JSONDocNode) => void;
    onCancel?: (actions: RichTextEditorActions) => void;
    mediaContext?: MediaContext;
    fabricEmojiUrl?: string;
    /** Used to display when the editor is loading locale translation messages */
    LoadingComponent?: React.ComponentType<LoadableLoadingComponentProps>;
    requestKey?: string;
    accountId?: string;
    shouldFocus?: boolean;
    fieldConfigs?: FieldConfigs;
    onInternalRender?: (actions: RichTextEditorActions) => void;
    ariaLabel?: string;
    ariaDescribedby?: string;
    customSecondaryToolbar?: (actions: EditorActions) => React.ReactElement;
    customMediaOptions?: MediaOptions;
}

interface RichTextEditorState {
    editorMessages: { [key: string]: string };
}

const autoformattingProvider = new PortalAutoformattingProvider();
const smartCardClient = new PortalCardClient();

export class RichTextEditorV2 extends React.Component<
    RichTextEditorProps & WrappedComponentProps,
    RichTextEditorState
> {
    static defaultProps = {
        LoadingComponent: () => null,
    };

    cardProvider = isUseNewEditorCardProviderEnabled()
        ? new PortalCardProviderNew()
        : new PortalCardProviderDeprecated();
    transformer = new JSONTransformer();
    editorActions?: EditorActions = undefined;
    mentionProvider = this.props.accountId
        ? Promise.resolve(getMentionProvider(this.props.accountId, this.props.requestKey, this.props.fieldConfigs))
        : undefined;

    componentDidMount() {
        this.props.shouldFocus && this.focusOnEditor();
    }

    componentDidUpdate(prevProps: RichTextEditorProps & WrappedComponentProps) {
        if (!prevProps.shouldFocus && this.props.shouldFocus) {
            this.focusOnEditor();
        }
    }

    focusOnEditor = () => {
        //we need to use the setTimeout here, otherwise focusing on the editor immediately will cause the caret to flash
        //50ms seem like our magic number, but it might varies on browsers
        setTimeout(() => {
            this.editorActions && this.editorActions.focus();
        }, 50);
    };

    onSave = (editorView: EditorView, actions: RichTextEditorActions) => {
        const { onSave } = this.props;
        if (onSave) {
            onSave(this.transformer.encode(editorView.state.doc), actions);
        }
    };

    renderEditorWithMediaContext = (mediaContext: MediaContext) => {
        const { portalId, requestId } = mediaContext;

        return (
            <WithUploadCredentials portalId={portalId} requestId={requestId}>
                {(provider) => this.renderEditorWithMediaProvider(provider)}
            </WithUploadCredentials>
        );
    };

    renderEditorWithMediaProvider = (mediaProvider: Promise<MediaProvider>) => {
        const mediaOptions = {
            provider: mediaProvider,
            allowMediaSingle: true,
        };
        return this.renderEditor(mediaOptions);
    };

    internalOnChange = (editorView: EditorView) => {
        const { onChange } = this.props;
        if (onChange) {
            onChange(this.transformer.encode(editorView.state.doc));
        }
    };

    renderEditor = (mediaOptions?: MediaOptions) => {
        const renderFunction = (actions: EditorActions) => this.internalRenderEditor(actions, mediaOptions);
        return (
            <EditorContext>
                <WithEditorActions render={renderFunction} />
            </EditorContext>
        );
    };

    internalOnCancel = () => {
        if (this.editorActions) {
            this.props.onCancel?.(this.editorActions);
        }
    };

    internalOnSave = (editorView: EditorView) => {
        if (this.editorActions) {
            this.onSave(editorView, this.editorActions);
        }
    };

    internalRenderEditor = (actions: EditorActions, mediaOptions?: MediaOptions) => {
        const {
            placeholderWhenCollapsed,
            isExpanded,
            expandEditor,
            placeholderWhenExpanded,
            onCancel,
            disabled,
            isEmojiEnabled,
            defaultValue,
            fabricEmojiUrl,
            onSave,
            ariaLabel,
            ariaDescribedby,
            customSecondaryToolbar,
        } = this.props;

        const internalOnCancel = onCancel === undefined ? undefined : () => this.internalOnCancel();

        const internalOnSave =
            onSave === undefined ? undefined : (editorView: EditorView) => this.internalOnSave(editorView);

        this.editorActions = actions;

        if (this.props.onInternalRender) {
            this.props.onInternalRender(this.editorActions);
        }

        const tableConfig = {
            allowColumnResizing: true,
            allowControls: true,
            allowHeaderRow: true,
            allowHeaderColumn: true,
            allowNumberColumn: true,
        };

        return (
            <EditorWrapper>
                <MediaWrapper mediaOptions={mediaOptions}>
                    <SmartCardProvider client={smartCardClient}>
                        <CollapsedEditor
                            placeholder={placeholderWhenCollapsed}
                            isExpanded={isExpanded}
                            onFocus={expandEditor}
                        >
                            <>
                                <AnalyticsContext data={{ attributes: { mediaSource: 'inline' } }}>
                                    <FabricEditor
                                        allowAnalyticsGASV3
                                        defaultValue={defaultValue}
                                        appearance="comment"
                                        placeholder={placeholderWhenExpanded}
                                        assistiveLabel={ariaLabel}
                                        assistiveDescribedBy={ariaDescribedby}
                                        onSave={internalOnSave}
                                        onCancel={internalOnCancel}
                                        onChange={this.internalOnChange}
                                        allowTextColor
                                        allowRule
                                        allowPanel
                                        allowTables={tableConfig}
                                        allowDate={false}
                                        UNSAFE_cards={{
                                            provider: Promise.resolve(this.cardProvider),
                                        }}
                                        autoformattingProvider={Promise.resolve(autoformattingProvider)}
                                        emojiProvider={
                                            __SERVER__ ? undefined : getEmojiProvider(isEmojiEnabled, fabricEmojiUrl)
                                        }
                                        media={mediaOptions}
                                        disabled={disabled}
                                        waitForMediaUpload
                                        mentionProvider={this.mentionProvider}
                                        // Upgrading @atlaskit/platform; fix these violations.
                                        // eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
                                        popupsBoundariesElement={document.documentElement}
                                    />
                                </AnalyticsContext>
                                {customSecondaryToolbar?.(this.editorActions)}
                            </>
                        </CollapsedEditor>
                    </SmartCardProvider>
                </MediaWrapper>
            </EditorWrapper>
        );
    };

    render() {
        const { editorType, mediaContext } = this.props;
        const editor = mediaContext
            ? this.renderEditorWithMediaContext(mediaContext)
            : this.renderEditor(this.props.customMediaOptions);

        return <ErrorBoundaryMessage type={editorType}>{editor}</ErrorBoundaryMessage>;
    }
}

export default withBrowserMetricSubmitter(BrowserMetricViewName.COMMENT_EDITOR_VIEW, injectIntl(RichTextEditorV2));

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const EditorWrapper = styled.div`
    .akEditor {
        /**
         * This is needed to prevent editor's table controls (+/x) to add/delete row,
         * getting partially hidden behind the text area.
         * We do not need the margin values of 'less-margin' class, which is defined here - https://bitbucket.org/atlassian/atlassian-frontend/src/91b21ecff04a782cf73f24fc8dfdf2bad7d2a904/packages/editor/editor-core/src/ui/Appearance/Comment/Comment.tsx?at=master&atlOrigin=eyJpIjoiMWRiZjlmZjhkYmE3NDg0Mzk3NWI3ODZhZjczNGQyODQiLCJwIjoiYmItY2hhdHMtaW50ZWdyYXRpb24ifQ#lines-217
         * A ticket has been created with editor team for this - https://product-fabric.atlassian.net/browse/DTR-855
         */
        .less-margin .ProseMirror {
            margin: ${token('space.150', '12px')};
            padding: 0 ${token('space.025', '2px')} ${token('space.025', '2px')};
        }
    }
`;
